import type { HTMLAttributes, ReactNode } from 'react';

import { cx } from '@/utils/cx';

/**
 * Canonical card-shell border: a rounded border in the secondary border color.
 *
 * Exported so every bordered card uses the SAME token and they can't drift —
 * `border border-secondary` and `border border-border-secondary` resolve to the
 * same color but are written two different ways across the app. New/edited
 * cards should compose `CARD_BORDER` (plus an optional background) rather than
 * re-spelling the border utilities.
 */
export const CARD_BORDER = 'rounded-lg border border-border-secondary';

const LIST_CARD_BACKGROUNDS = {
  transparent: '',
  primary: 'bg-bg-primary',
  secondary: 'bg-bg-secondary',
} as const;

export type ListCardBackground = keyof typeof LIST_CARD_BACKGROUNDS;

export interface ListCardProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'background'
> {
  /** Rows to render in the list. Each direct child becomes a divided row. */
  children: ReactNode;
  /**
   * Card background fill. Defaults to `'transparent'` (no fill — the page
   * background shows through). Use `'secondary'` for a filled card that sits on
   * a primary-background surface.
   */
  background?: ListCardBackground;
}

/**
 * ListCard — a bordered, rounded container that lays its children out as a
 * vertical list with a hairline divider between each item.
 *
 * This is a pure layout primitive: it owns only the card shell (rounded border)
 * and the inter-row dividers. It does not pad, style, or make its rows
 * interactive — each child supplies its own content and padding. Use it
 * directly whenever you want a list-in-a-card of arbitrary content.
 *
 * For a list whose rows are themselves clickable actions, use `ButtonList`,
 * which composes `ListCard` and adds `ButtonList.Item`.
 *
 * This is deliberately NOT a table: it has no column model, header, or cell
 * grid. Reach for `Table` only when you have genuinely tabular data.
 */
export function ListCard({
  children,
  className,
  background = 'transparent',
  ...props
}: ListCardProps) {
  return (
    <div
      {...props}
      className={cx(
        'overflow-hidden',
        CARD_BORDER,
        LIST_CARD_BACKGROUNDS[background],
        className
      )}
    >
      <div className='divide-y divide-border-secondary'>{children}</div>
    </div>
  );
}
ListCard.displayName = 'ListCard';
